import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { loadMessageTemplateById } from '../store/templatesSlice';
import { ROUTES } from '../../../shared/config/routes';
import { CHANNEL } from '../constants/messageTemplateKeys';
import { mapTemplateToBasicInfoForm } from '../lib/mapTemplateToBasicInfoForm';
import { extractCustomStyle, extractEditorContent } from '../lib/stripTemplateContent';
import { plainTextToHtml } from '../lib/plainTextToHtml';
import { buildCopyTemplateName } from '../lib/buildCopyTemplateName';

export function useTemplateSourceLoader({
  isEditMode,
  isCopyMode = false,
  hasTemplateId,
  templateId,
  apiReady,
  channelKey,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const loadsSourceTemplate = (isEditMode || isCopyMode) && hasTemplateId;
  const [isSourceDataLoading, setIsSourceDataLoading] = useState(() => loadsSourceTemplate);
  const [initialBasicInfoForm, setInitialBasicInfoForm] = useState(null);
  const [initialEditorContent, setInitialEditorContent] = useState('');
  const [initialCustomStyle, setInitialCustomStyle] = useState('');
  const [initialCheckedDisclosureIds, setInitialCheckedDisclosureIds] = useState([]);
  const [initialVariableIds, setInitialVariableIds] = useState([]);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [modified, setModified] = useState(null);
  const [lastEditedBy, setLastEditedBy] = useState(null);

  useEffect(() => {
    if (!loadsSourceTemplate || !apiReady) return;

    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsSourceDataLoading(true);

    dispatch(loadMessageTemplateById({ templateId }))
      .unwrap()
      .then((template) => {
        if (cancelled) return;
        const basicInfoForm = mapTemplateToBasicInfoForm(template);
        setInitialBasicInfoForm(
          isCopyMode
            ? { ...basicInfoForm, templateName: buildCopyTemplateName(basicInfoForm.templateName) }
            : basicInfoForm,
        );
        // A copy has no saved state of its own, so it inherits neither the status nor the edit
        // history of its source — both stay unset until the new template is saved.
        if (!isCopyMode) {
          setCurrentStatus(template?.status ?? null);
          setModified(template?.modified ?? null);
          setLastEditedBy(template?.last_edited_by ?? null);
        }
        const rawContent =
          channelKey === CHANNEL.EMAIL
            ? String(template?.html_content ?? '')
            : plainTextToHtml(template?.text_content);
        const content = extractEditorContent(rawContent);
        setInitialEditorContent(content);
        // Preserve any responsive/custom CSS (e.g. @media from migrated MJML) so it survives
        // the extract→rebuild round-trip. Only email templates carry a shell with styles.
        setInitialCustomStyle(channelKey === CHANNEL.EMAIL ? extractCustomStyle(rawContent) : '');
        if (Array.isArray(template?.disclosures)) {
          setInitialCheckedDisclosureIds(template.disclosures.map((d) => d.id));
        }
        if (Array.isArray(template?.variables)) {
          setInitialVariableIds(template.variables.map((v) => v.id));
        }
      })
      .catch((error) => {
        if (cancelled) return;
        if (error?.status === 404) {
          navigate(ROUTES.TEMPLATES, { replace: true });
        }
      })
      .finally(() => {
        if (!cancelled) setIsSourceDataLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [apiReady, dispatch, loadsSourceTemplate, isCopyMode, templateId, channelKey, navigate]);

  return {
    isSourceDataLoading,
    initialBasicInfoForm,
    initialEditorContent,
    initialCustomStyle,
    initialCheckedDisclosureIds,
    initialVariableIds,
    currentStatus,
    modified,
    lastEditedBy,
  };
}
